import {
  useEffect,
  useState,
} from 'react'

import acquireAccessToken from './acquireAccessToken'
import hasAccessToken from './hasAccessToken'


/**
 * Acquires an access token before any protected request goes out, unless one
 * is already held.
 *
 * The token lives in memory only, so every page load starts without one. The
 * first load after a sign-in collects it from the one-time handoff; any other
 * load, and a handoff that turns up empty, trades the refresh cookie for a new
 * one instead.
 *
 * Reports readiness rather than success. A visitor who has never signed in has
 * no refresh cookie, and one whose session has ended has a cookie the server
 * no longer accepts. Both carry on unauthenticated exactly as they would with
 * no token at all, and what that means is for the application to decide.
 *
 * @returns {Object} with the shape { isReady }
 */
export default function useAccessTokenBootstrap() {
  // A token already held means an earlier mount on this page load has done
  // the work, so this one reports ready on its first render and gives up no
  // paint.
  const [isReady, setReady] = useState(hasAccessToken)

  useEffect(() => {
    if ( isReady ) {
      return
    }

    acquireAccessToken()
      .catch(() => {
        // Swallowed, because a failed attempt leaves the visitor exactly where
        // having no token would, and there is nothing further to do about it
        // here.
      })
      .finally(() => {
        // Readiness is unconditional: it reports that the attempt is over, not
        // that a token was collected. Nothing above this renders until it
        // flips, so letting a rejection skip it would trade a missing token
        // for a blank application.
        setReady(true)
      })
  }, [isReady])

  return {
    isReady,
  }
}
